import React from "react";
import {Box, Typography, useMediaQuery, useTheme} from "@mui/material";

/**
 * SMBestSellerClaim
 *
 * Interactive circular claim badge.
 *
 * - Desktop: flips on hover with subtle zoom
 * - Touch devices: tap to flip, tap outside to close
 * - Back image darkened with branded border for readability
 * - Text fades in after flip settles to stay crisp
 * - Fully responsive and optimized for tight horizontal layouts
 */
export default function SMBestSellerClaim({
                                              frontImageSrc,
                                              backImageSrc,
                                              backText = "This is the back of the text.",
                                              alt = "San Miguel Foods Claim",
                                              size = 220,
                                              borderRadius = 9999,
                                              overlayAlign = "center",
                                              overlayTextColor = "#fff",
                                              overlayTextShadow = "0 2px 10px rgba(0,0,0,0.6)",
                                              overlayFontSize = { xs: 11, sm: 11, md: 14, lg: 18, xl: 24 },
                                              overlayFontWeight = 900,
                                              overlayLineHeight = 1.15,
                                              backFaceScale = { xs: 1.4, sm: 1.5, md: 1.5, lg: 1.5, xl: 1.5 },
                                              pinned = false,
                                              onPinnedChange,

                                              sx,
                                          }) {
    const canHover = useMediaQuery("(hover: hover) and (pointer: fine)");
    const [hovered, setHovered] = React.useState(false);

    // Desktop: hover only. Touch: pinned only.
    const isFlipped = canHover ? hovered : pinned;

    const handleClick = () => {
        // Desktop click does nothing
        if (canHover) return;
        onPinnedChange?.(!pinned);
    };

    const overlayPositionSx =
        overlayAlign === "bottom"
            ? { alignItems: "flex-end", pb: 2.25 }
            : overlayAlign === "top"
                ? { alignItems: "flex-start", pt: 2.25 }
                : { alignItems: "center" };


    const textShowDelayMs = 140;

    const theme = useTheme();

    const upXl = useMediaQuery(theme.breakpoints.up("xl"));
    const upLg = useMediaQuery(theme.breakpoints.up("lg"));
    const upMd = useMediaQuery(theme.breakpoints.up("md"));
    const upSm = useMediaQuery(theme.breakpoints.up("sm"));

    const resolvedBackFaceScale = upXl
        ? backFaceScale.xl ?? backFaceScale.lg ?? backFaceScale.md ?? backFaceScale.sm ?? backFaceScale.xs
        : upLg
            ? backFaceScale.lg ?? backFaceScale.md ?? backFaceScale.sm ?? backFaceScale.xs
            : upMd
                ? backFaceScale.md ?? backFaceScale.sm ?? backFaceScale.xs
                : upSm
                    ? backFaceScale.sm ?? backFaceScale.xs
                    : backFaceScale.xs;


    return (
        <Box
            role="button"
            aria-label={alt}
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleClick();
            }}
            onMouseEnter={canHover ? () => setHovered(true) : undefined}
            onMouseLeave={canHover ? () => setHovered(false) : undefined}
            sx={{
                width: typeof size === "number" ? `${size}px` : size,
                height: typeof size === "number" ? `${size}px` : size,
                position: "relative",
                zIndex: isFlipped ? 9999 : 1,
                cursor: "pointer",
                outline: "none",
                WebkitTapHighlightColor: "transparent",
                perspective: 1200,
                display: "inline-block",
                ...sx,

                ...(canHover && {
                    "&:hover .SMBSC-ring": { transform: "translateY(-1px)" },
                }),
            }}
        >
            <Box
                className="SMBSC-ring"
                sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius,
                    position: "relative",
                    boxShadow: "none",
                    background: "transparent",
                    transition: "transform 450ms cubic-bezier(0.2, 0.9, 0.2, 1)",
                }}
            >
                <Box
                    className="SMBSC-inner"
                    sx={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        transformStyle: "preserve-3d",
                        transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                        transition: canHover && hovered
                            ? "transform 900ms cubic-bezier(0.2, 0.9, 0.2, 1)"
                            : "transform 700ms cubic-bezier(0.2, 0.9, 0.2, 1)",
                        borderRadius,
                        willChange: "transform",
                    }}
                >
                    <Face borderRadius={borderRadius} imageSrc={frontImageSrc} alt={alt} rotateY={false} />

                    <Face
                        borderRadius={borderRadius}
                        imageSrc={backImageSrc}
                        alt={alt}
                        rotateY
                        active={isFlipped}
                        backFaceScale={resolvedBackFaceScale}
                        objectFit="cover"
                        imageScale={1}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                justifyContent: "center",
                                textAlign: "center",
                                px: { xs: 0.5, sm: 1 },
                                ...overlayPositionSx,
                            }}
                        >
                            <Typography
                                sx={{
                                    maxWidth: { xs: "90%", md: "95%" },
                                    color: overlayTextColor,
                                    textShadow: overlayTextShadow,
                                    fontSize: overlayFontSize,
                                    fontWeight: overlayFontWeight,
                                    lineHeight: overlayLineHeight,
                                    whiteSpace: "pre-line",

                                    // keep the crisp behavior
                                    opacity: isFlipped ? 1 : 0,
                                    transition: "opacity 120ms ease",
                                    transitionDelay: isFlipped ? `${textShowDelayMs}ms` : "0ms",
                                }}
                            >
                                {backText}
                            </Typography>
                        </Box>
                    </Face>
                </Box>
            </Box>
        </Box>
    );
}

function Face({
                  imageSrc,
                  alt,
                  rotateY = false,
                  borderRadius,
                  active = false,
                  backFaceScale = 1.6,
                  objectFit = "contain",
                  imageScale = 1.3,
                  children,
              }) {
    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                borderRadius,
                overflow: "visible",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                transformOrigin: "center",
                transition: rotateY
                    ? "transform 900ms cubic-bezier(0.2, 0.9, 0.2, 1)"
                    : "none",
                transform: rotateY
                    ? active
                        ? `rotateY(180deg) scale(${backFaceScale})`
                        : "rotateY(180deg) scale(1)"
                    : "rotateY(0deg)",
                zIndex: rotateY ? (active ? 10 : 1) : 2,
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    borderRadius,
                    overflow: "hidden",
                    ...(rotateY && {
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(rgba(0,0,0,0.50), rgba(0,0,0,0.50))",
                            zIndex: 1,
                            pointerEvents: "none",
                        },
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            borderRadius,
                            boxShadow: "inset 0 0 0 3px #790A0A",
                            pointerEvents: "none",
                            zIndex: 3,
                        },
                    }),
                }}
            >
                <Box
                    component="img"
                    src={imageSrc}
                    alt={alt}
                    loading="lazy"
                    draggable={false}
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        userSelect: "none",
                        objectFit,

                        // keep your “bigger inside” behavior for contain
                        transform: imageScale !== 1 ? `scale(${imageScale})` : "none",
                        transformOrigin: "center",
                    }}
                />

                <Box sx={{ position: "absolute", inset: 0, zIndex: 2 }}>{children}</Box>
            </Box>
        </Box>
    );
}
